import React, { useRef, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Modal,
  Stack,
  Typography,
} from "@mui/joy";
import LogoutIcon from "@mui/icons-material/Logout";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import CloseIcon from "@mui/icons-material/Close";
import { useAuthStore } from "../../store/authStore";
import { FollowButton } from "./FollowButton";

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

interface ProfileHeaderProps {
  targetProfile?: {
    id: string;
    username: string;
    full_name: string | null;
    avatar_url: string | null;
    bio: string | null;
  };
  isOwnProfile?: boolean;
}

export const ProfileHeader: React.FC<ProfileHeaderProps> = ({
  targetProfile,
  isOwnProfile = true,
}) => {
  const { profile, signOut, uploadAvatar } = useAuthStore();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [enlargedOpen, setEnlargedOpen] = useState(false);

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return "Good morning";
    if (hour < 18) return "Good afternoon";
    return "Good evening";
  };

  const displayProfile = targetProfile || profile;
  const displayName =
    displayProfile?.full_name || displayProfile?.username || "Traveler";

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      console.error("Invalid file type. Please select an image.");
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      console.error("File too large. Maximum size is 5MB.");
      return;
    }

    const previewUrl = URL.createObjectURL(file);
    setAvatarPreview(previewUrl);
    setUploading(true);

    try {
      await uploadAvatar(file);
      URL.revokeObjectURL(previewUrl);
      setAvatarPreview(null);
    } catch (error) {
      console.error("Failed to upload avatar:", error);
      setAvatarPreview(null);
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    fileInputRef.current?.click();
  };

  const avatarSrc = avatarPreview || displayProfile?.avatar_url || undefined;
  const hasAvatar = !!avatarSrc;

  return (
    <>
      <Stack direction="row" spacing={2} alignItems="center" data-testid="profile-header">
        <Box
          position="relative"
          sx={{ cursor: hasAvatar ? "pointer" : undefined }}
          onClick={hasAvatar ? () => setEnlargedOpen(true) : undefined}
          data-testid="avatar-wrapper"
        >
          <Avatar
            src={avatarSrc}
            alt={displayName}
            sx={{ width: 80, height: 80, fontSize: "2rem" }}
          >
            {displayName.charAt(0).toUpperCase()}
          </Avatar>
          {isOwnProfile && (
            <>
              <Box
                data-testid="avatar-edit-overlay"
                onClick={handleEditClick}
                sx={{
                  position: "absolute",
                  bottom: -2,
                  right: -2,
                  width: 28,
                  height: 28,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "background.surface",
                  border: "2px solid",
                  borderColor: "divider",
                  cursor: "pointer",
                  "&:hover": {
                    backgroundColor: "neutral.100",
                  },
                }}
              >
                {uploading ? (
                  <CircularProgress size="sm" sx={{ "--CircularProgress-size": "16px" }} />
                ) : (
                  <CameraAltIcon sx={{ fontSize: "0.9rem", color: "text.secondary" }} />
                )}
              </Box>
              <input
                type="file"
                ref={fileInputRef}
                accept="image/*"
                style={{ display: "none" }}
                onChange={handleFileSelect}
                data-testid="avatar-file-input"
              />
            </>
          )}
        </Box>
        <Box flex={1}>
          {isOwnProfile ? (
            <>
              <Typography level="h3">
                {getGreeting()}, {displayName}!
              </Typography>
              <Typography level="body-sm" sx={{ color: "text.secondary" }}>
                Your travel passport
              </Typography>
            </>
          ) : (
            <>
              <Typography level="h3">{displayName}</Typography>
              <Typography level="body-sm" sx={{ color: "text.secondary" }}>
                @{displayProfile?.username}
              </Typography>
              {displayProfile?.bio && (
                <Typography level="body-sm" sx={{ marginTop: 1 }}>
                  {displayProfile.bio}
                </Typography>
              )}
            </>
          )}
        </Box>
        {!isOwnProfile && targetProfile && (
          <FollowButton targetUserId={targetProfile.id} />
        )}
        {isOwnProfile && (
          <Button
            variant="outlined"
            color="neutral"
            size="sm"
            startDecorator={<LogoutIcon />}
            onClick={signOut}
          >
            Sign Out
          </Button>
        )}
      </Stack>

      {/* Enlarged avatar modal */}
      <Modal
        open={enlargedOpen}
        onClose={() => setEnlargedOpen(false)}
        data-testid="avatar-enlarged-modal"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box
          sx={{
            position: "relative",
            outline: "none",
            maxWidth: "90vw",
            maxHeight: "90vh",
          }}
        >
          <IconButton
            onClick={() => setEnlargedOpen(false)}
            data-testid="avatar-enlarged-close"
            sx={{
              position: "absolute",
              top: -40,
              right: 0,
              color: "white",
              "&:hover": { backgroundColor: "rgba(255,255,255,0.1)" },
            }}
          >
            <CloseIcon />
          </IconButton>
          <Box
            component="img"
            src={avatarSrc}
            alt={displayName}
            sx={{
              maxWidth: "80vw",
              maxHeight: "80vh",
              objectFit: "contain",
              borderRadius: "sm",
            }}
          />
          {isOwnProfile && (
            <Button
              variant="soft"
              color="neutral"
              size="sm"
              startDecorator={<CameraAltIcon />}
              onClick={(e) => {
                handleEditClick(e);
                setEnlargedOpen(false);
              }}
              data-testid="avatar-enlarged-change"
              sx={{
                position: "absolute",
                bottom: 12,
                right: 12,
              }}
            >
              Change Photo
            </Button>
          )}
        </Box>
      </Modal>
    </>
  );
};
